import { ALL_PERMISSIONS, FLOOR, closure, def, resolve } from "@dentacore/shared-types";
import type {
  AccessOverrideView,
  AccessRoleView,
  Effect,
  Job,
  ModuleKey,
  Permission,
} from "@dentacore/shared-types";

/** Every permission in one module, in the catalogue's order. */
export function permissionsIn(module: ModuleKey): Permission[] {
  return ALL_PERMISSIONS.filter((p) => def(p).module === module);
}

export interface ModuleLevel {
  text: string;
  tone: "full" | "none" | "partial";
  held: number;
  total: number;
}

/** "Full", "View", "3 of 7" or "None" — how much of a module somebody reaches. */
export function moduleLevel(held: ReadonlySet<Permission>, module: ModuleKey): ModuleLevel {
  const all = permissionsIn(module);
  const mine = all.filter((p) => held.has(p));
  if (mine.length === 0) return { text: "None", tone: "none", held: 0, total: all.length };
  if (mine.length === all.length)
    return { text: "Full", tone: "full", held: mine.length, total: all.length };
  if (mine.every((p) => p.endsWith(".view")))
    return { text: "View", tone: "partial", held: mine.length, total: all.length };
  return {
    text: `${mine.length} of ${all.length}`,
    tone: "partial",
    held: mine.length,
    total: all.length,
  };
}

/**
 * How much of a module a role carries, as the role editor offers it: None,
 * View, Full — or Custom, when somebody has ticked individual actions.
 *
 * The safety floor is left out of the sum because everybody holds it: counting
 * it would make every role's Patients read "View" before anything was chosen.
 */
export type ModuleChoice = "none" | "view" | "full" | "custom";

/** A module's permissions an administrator can choose — everything but the floor. */
export function choosableIn(module: ModuleKey): Permission[] {
  return permissionsIn(module).filter((p) => def(p).floor !== true);
}

/** The "see it" permissions of a module, which is what View gives. Empty for Settings. */
export function viewsIn(module: ModuleKey): Permission[] {
  return choosableIn(module).filter((p) => p.endsWith(".view"));
}

export function moduleChoice(held: (p: Permission) => boolean, module: ModuleKey): ModuleChoice {
  const all = choosableIn(module);
  const mine = all.filter(held);
  if (mine.length === 0) return "none";
  if (mine.length === all.length) return "full";
  const views = viewsIn(module);
  if (views.length > 0 && mine.length === views.length && views.every(held)) return "view";
  return "custom";
}

/** `listed` with one module set to None, View or Full, the other modules untouched. */
export function withChoice(
  listed: ReadonlySet<Permission>,
  module: ModuleKey,
  choice: "none" | "view" | "full",
): Set<Permission> {
  const next = new Set(listed);
  for (const p of permissionsIn(module)) next.delete(p);
  const add = choice === "full" ? choosableIn(module) : choice === "view" ? viewsIn(module) : [];
  for (const p of add) next.add(p);
  return next;
}

/**
 * What a list of permissions brings with it (`implies`), excluding itself —
 * shown ticked and fixed in the role editor, with what it comes with.
 */
export function impliedBy(listed: ReadonlySet<Permission>): Map<Permission, Permission> {
  const implied = new Map<Permission, Permission>();
  for (const p of listed) {
    for (const q of closure(p)) {
      if (q !== p && !listed.has(q) && !implied.has(q)) implied.set(q, p);
    }
  }
  return implied;
}

/** A job's words, as the person page already writes them. */
export function jobLabel(job: string): string {
  const words = job.toLowerCase().split("_");
  return words
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

// ── what a change does, said before it is saved (2026-10-04) ────────────────

/** What a role carries, the way the server reads it: CURRENT_ACCESS from the catalogue. */
export function rolePermissions(role: AccessRoleView): Permission[] {
  return role.kind === "CURRENT_ACCESS" ? role.effective : role.permissions;
}

/** A set of permissions with everything they bring, and the safety floor. */
export function withClosure(permissions: Iterable<Permission>): Set<Permission> {
  const out = new Set<Permission>(FLOOR);
  for (const p of permissions) for (const q of closure(p)) out.add(q);
  return out;
}

/**
 * The role someone holds at a location nobody gave them one (R-D6): the role
 * that starts their job, or the clinic's fallback. The server decides the same
 * way, oldest role first.
 */
export function jobRole(roles: readonly AccessRoleView[], job: string): AccessRoleView | undefined {
  return (
    roles.find((r) => r.defaultForJobs.includes(job as Job)) ?? roles.find((r) => r.isFallback)
  );
}

/** What somebody could do with these roles, their administrator flag and their live exceptions. */
export function accessWith(
  roles: readonly AccessRoleView[],
  isAdministrator: boolean,
  overrides: readonly AccessOverrideView[],
): Set<Permission> {
  const access = resolve({
    roles: [
      ...(isAdministrator ? [{ name: "Administrator", permissions: ALL_PERMISSIONS }] : []),
      ...roles.map((r) => ({ name: r.name, permissions: rolePermissions(r) })),
    ],
    overrides: overrides
      .filter((o) => o.live)
      .map((o) => ({
        permission: o.permission,
        effect: o.effect,
        reason: o.reason,
        grantedBy: o.grantedByName,
        expiresAt: o.expiresAt === null ? null : new Date(o.expiresAt),
      })),
    now: new Date(),
  });
  return new Set(access.permissions);
}

export interface Impact {
  gain: string[];
  lose: string[];
}

/** The difference between two sets of permissions, in words an administrator reads. */
export function impactOf(before: ReadonlySet<Permission>, after: ReadonlySet<Permission>): Impact {
  return {
    gain: ALL_PERMISSIONS.filter((p) => after.has(p) && !before.has(p)).map((p) => def(p).label),
    lose: ALL_PERMISSIONS.filter((p) => before.has(p) && !after.has(p)).map((p) => def(p).label),
  };
}

/** Somebody whose role changes, now, because a job moves between roles (R-D6a). */
export interface Affected {
  userId: string;
  name: string;
  branchName: string;
  from: string;
  to: string;
}

/**
 * Who changes role **now** if `roleId` (null: a role being made, called
 * `newName`) is to start exactly `newJobs` — everyone holding a role through
 * their job, at a location where nobody gave them one, whose job moves.
 * Read from the holders the server listed, so it is the server's own answer.
 */
export function jobMoveAffected(
  roles: readonly AccessRoleView[],
  jobsOf: ReadonlyMap<string, string>,
  roleId: string | null,
  newJobs: readonly Job[],
  newName: string,
): Affected[] {
  const self = roles.find((r) => r.id === roleId);
  const oldJobs = self?.defaultForJobs ?? [];
  const fallback = roles.find((r) => r.isFallback);
  const out: Affected[] = [];
  for (const job of new Set<Job>([...oldJobs, ...newJobs])) {
    const before = roles.find((r) => r.defaultForJobs.includes(job)) ?? fallback;
    const after = newJobs.includes(job)
      ? { id: roleId ?? "new", name: newName }
      : oldJobs.includes(job)
        ? fallback
        : before;
    if (before === undefined || after === undefined || before.id === after.id) continue;
    for (const h of before.holderNames) {
      if (h.isDefault && jobsOf.get(h.userId) === job)
        out.push({
          userId: h.userId,
          name: h.name,
          branchName: h.branchName,
          from: before.name,
          to: after.name,
        });
    }
  }
  return out;
}

// ── one person's part, changed from their grid (PA-D1–PA-D3, STORY-193) ─────

/** What Save on a person's part does: exceptions to end, and exceptions to add. */
export interface ExceptionPlan {
  end: AccessOverrideView[];
  add: { permission: Permission; effect: Effect }[];
}

/**
 * The exceptions that make `want` true at a location, **undoing before
 * adding** (PA-D2): an action turned back to what their roles give ends the
 * exception that changed it, and one turned away from it ends any opposite
 * exception before adding its own. An action already the way it is wanted is
 * left alone, so a switch flicked twice changes nothing.
 *
 * `fromRoles` is what their roles give at the location; `heldNow` what they
 * hold there today, exceptions included; `live` their live exceptions.
 */
export function planExceptions(
  want: ReadonlyMap<Permission, boolean>,
  fromRoles: ReadonlySet<Permission>,
  heldNow: ReadonlySet<Permission>,
  live: readonly AccessOverrideView[],
): ExceptionPlan {
  const plan: ExceptionPlan = { end: [], add: [] };
  for (const [permission, on] of want) {
    if (on === heldNow.has(permission)) continue;
    const existing = live.find((o) => o.live && o.permission === permission);
    const needed: Effect | null = on === fromRoles.has(permission) ? null : on ? "grant" : "revoke";
    if (existing !== undefined && existing.effect === needed) continue;
    if (existing !== undefined) plan.end.push(existing);
    if (needed !== null) plan.add.push({ permission, effect: needed });
  }
  return plan;
}

/** Their live exceptions as they would be once `plan` is saved — what the dialog draws from. */
export function withPlan(
  live: readonly AccessOverrideView[],
  plan: ExceptionPlan,
): AccessOverrideView[] {
  const ended = new Set(plan.end.map((o) => o.id));
  return [
    ...live.filter((o) => o.live && !ended.has(o.id)),
    ...plan.add.map((x, i) => ({
      id: `planned-${String(i)}`,
      userId: "",
      userName: "",
      permission: x.permission,
      effect: x.effect,
      reason: "",
      expiresAt: null,
      grantedByName: "",
      createdAt: "",
      live: true,
    })),
  ];
}
